import {
  reactivateStaffService,
  addStaffService,
  deactivateStaffService,
  editStaffService,
  getStaffDetails,
  getStaffList,
  resetStaffPasswordService,
  changeStaffEmailService,
  startStaffUpdateService,
  changeStaffPhoneService,
  getStaffListForSelectionService,
} from '@/services/staff';
import {
  UpdateEmailFields,
  UpdatePasswordFields,
  UpdatePhoneFields,
} from '@/types/common';
import { SelectOption } from '@/types/inputs';
import { queryKeys, StaffFormEditValues, StaffFormValues } from '@/types/staff';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuthUser } from '../stores/useAuthUser';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.details],
  });
}

export const useGetStaffList = () => {
  const [searchParams] = useSearchParams();
  const params = searchParams.toString();

  const { data, isPending, isSuccess } = useQuery({
    queryKey: [queryKeys.list, params],
    queryFn: () => getStaffList(params),
  });

  return {
    isSuccess,
    staffList: data?.records || [],
    totalRecordCount: data?.totalRecordCount || 0,
    isStaffListPending: isPending,
  };
};

export const useGetStaffDetails = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.details, uuid],
    queryFn: () => getStaffDetails(uuid),
    enabled: !!uuid,
  });

  return {
    staffDetails: data,
    isStaffDetailsPending: isPending,
  };
};

export const useGetStaffListForSelection = (enabled = true) => {
  const { isStaff } = useAuthUser();
  const { data: staffList = [], isPending: isStaffListPending } = useQuery({
    queryFn: () => getStaffListForSelectionService(),
    queryKey: [queryKeys.list, queryKeys.selectionList],
    select: data => {
      const mappedItems: SelectOption[] = data
        ? data.map(staff => {
            return {
              ...staff,
              value: staff.uuid,
              label: staff.fullName,
            };
          })
        : [];

      return mappedItems;
    },
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: enabled && isStaff,
  });

  return { staffList, isStaffListPending };
};

export function useAddStaff() {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: StaffFormValues) =>
      addStaffService({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    addStaff: mutateAsync,
    isAddStaffPending: isPending,
  };
}

export function useEditStaff() {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: StaffFormEditValues) =>
      editStaffService({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    editStaff: mutateAsync,
    isEditStaffPending: isPending,
  };
}

export const useDeactivateStaff = (uuid: string, enabled = false) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => deactivateStaffService(uuid, false),
    onSuccess: () => clearQueryCache(queryClient),
  });
  const { data, isFetching: isCanDeactivateStaffPending } = useQuery({
    queryKey: [queryKeys.preflight, uuid],
    queryFn: () => deactivateStaffService(uuid, true),
    enabled,
  });

  return {
    canDeactivateStaff: data?.mustReplaceStaff === false || false,
    isCanDeactivateStaffPending,
    deactivateStaff: mutateAsync,
    isDeactivateStaffPending: isPending,
  };
};

export function useReactivateStaff(uuid: string) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => reactivateStaffService(uuid),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    reactivateStaff: mutateAsync,
    isReactivateStaffPending: isPending,
  };
}

export function useResetStaffPassword(uuid: string) {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: UpdatePasswordFields) =>
      resetStaffPasswordService({ ...data, uuid }),
  });

  return {
    resetStaffPassword: mutateAsync,
    isResetStaffPasswordPending: isPending,
  };
}

export function useStartStaffUpdate(uuid: string) {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => startStaffUpdateService(uuid),
  });

  return {
    startStaffUpdate: mutateAsync,
    isStartStaffUpdatePending: isPending,
  };
}

export function useChangeStaffEmail(uuid: string) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: UpdateEmailFields) =>
      changeStaffEmailService({ ...data, uuid }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    changeStaffEmail: mutateAsync,
    isChangeStaffEmailPending: isPending,
  };
}

export function useChangeStaffPhone(uuid: string) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: UpdatePhoneFields) =>
      changeStaffPhoneService({ ...data, uuid }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    changeStaffPhone: mutateAsync,
    isChangeStaffPhonePending: isPending,
  };
}
